/**
 * El nombre de un BCBP viaja en un campo fijo de 20 caracteres con formato
 * `APELLIDO/NOMBRES`: llega siempre en mayúsculas y, cuando no cabe, cortado a
 * la mitad —"GIBBONS/ANISSA PATRI" es en realidad "Anissa Patricia"—. Para
 * saludar usamos sólo el primer nombre de pila, que es el único token al que
 * el recorte casi nunca llega.
 */

/**
 * Tratamientos que algunas aerolíneas pegan al nombre sin espacio ("JOHNMR").
 * Cuando vienen separados por espacio no hace falta tocarlos: al quedarnos con
 * el primer token ya se descartan solos.
 *
 * Se omiten a propósito "MS" y "DR": hay nombres reales que terminan así
 * (WILLIAMS, ADAMS, ALEKSANDR) y recortarlos estropearía el saludo más de lo
 * que lo arreglaría. Orden de mayor a menor para que "MRS" no case como "MR".
 */
const ATTACHED_TITLES = ["MSTR", "MISS", "MRS", "MR"];

function stripAttachedTitle(given: string): string {
  for (const title of ATTACHED_TITLES) {
    // El resto tiene que seguir pareciendo un nombre, no dos letras sueltas.
    if (given.endsWith(title) && given.length - title.length >= 3) {
      return given.slice(0, -title.length);
    }
  }
  return given;
}

/** "ANISSA" → "Anissa", "O'BRIEN" → "O'Brien", "ANA-MARIA" → "Ana-Maria". */
function toTitleCase(name: string): string {
  return name
    .toLocaleLowerCase("es")
    .replace(/(^|[\s'’-])(\p{L})/gu, (_, sep: string, letter: string) =>
      sep + letter.toLocaleUpperCase("es"),
    );
}

/**
 * Primer nombre de pila listo para saludar, o `null` si el boarding pass no
 * trae algo con lo que se pueda tutear a alguien (sin "/" no se sabe qué parte
 * es el apellido, y preferimos un saludo genérico antes que uno equivocado).
 */
export function greetingName(passengerName: string | undefined): string | null {
  if (!passengerName) return null;

  const slash = passengerName.indexOf("/");
  if (slash === -1) return null;

  const given = passengerName.slice(slash + 1).trim();
  if (!given) return null;

  const first = stripAttachedTitle(given.split(/\s+/)[0] ?? "");
  // Fuera dígitos y basura de relleno; deja letras, apóstrofos y guiones.
  const clean = first.replace(/[^\p{L}'’-]/gu, "");

  return clean.length >= 2 ? toTitleCase(clean) : null;
}
